// Auto-lock: borra el material criptográfico de memoria al pasar a segundo plano
// o por inactividad, y devuelve la app a la pantalla de desbloqueo. Cierra el
// flanco "robo del móvil DESBLOQUEADO con IKOGNIT abierto" que la auditoría marcó
// (fallo #4): sin esto, wrappingKey/privateKey/DEKs viven en RAM indefinidamente.
//
// Núcleo AISLADO y testeable: la app registra (a) funciones de wipe del material
// vivo y (b) un callback de bloqueo (volver a 'unlock'); installAutoLock cablea
// los eventos de plataforma. Móvil (Capacitor) llama lockNow('background') desde
// el appStateChange nativo.

import sodium from 'libsodium-wrappers-sumo';

type WipeFn = () => void;

const wipes = new Set<WipeFn>();
let onLock: (() => void) | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let idleMs = 0;
let installed = false;

/** Registra una función que borra material sensible al bloquear. Devuelve un
 *  de-registrador. */
export function registerWipe(fn: WipeFn): () => void {
  wipes.add(fn);
  return () => { wipes.delete(fn); };
}

/** Callback que la app conecta a "volver a la pantalla de desbloqueo". */
export function setOnLock(fn: (() => void) | null): void {
  onLock = fn;
}

/** Sobrescribe con ceros los buffers indicados (best-effort; JS no garantiza
 *  borrado total del heap, pero reduce la ventana de exposición). */
export function secureWipe(...bufs: Array<Uint8Array | null | undefined>): void {
  for (const b of bufs) {
    if (b && b.length) {
      try { sodium.memzero(b); } catch { try { b.fill(0); } catch { /* */ } }
    }
  }
}

/** Bloquea YA: ejecuta todos los wipes registrados y avisa a la app. */
export function lockNow(_reason: string): void {
  const fns = Array.from(wipes);
  wipes.clear();
  for (const w of fns) { try { w(); } catch { /* */ } }
  if (timer) { clearTimeout(timer); timer = null; }
  const cb = onLock;
  if (cb) cb();
}

function resetIdle(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  if (idleMs > 0) timer = setTimeout(() => lockNow('inactivity'), idleMs);
}

/** Arma (o rearma) el bloqueo por inactividad. 0 = desactivado. */
export function armInactivity(seconds: number): void {
  idleMs = Math.max(0, Math.floor(seconds)) * 1000;
  resetIdle();
}

/** Señala actividad del usuario: reinicia el contador de inactividad. */
export function noteActivity(): void {
  if (idleMs > 0) resetIdle();
}

/** Desarma el auto-lock (p. ej. al hacer logout manual). */
export function disarm(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  idleMs = 0;
}

/**
 * Cablea los eventos de plataforma (web/escritorio). Idempotente.
 *  - inactividad: cualquier interacción reinicia el contador.
 *  - segundo plano: al ocultarse la pestaña/ventana, bloqueo inmediato.
 * En móvil, la capa nativa (Capacitor App) debe llamar lockNow('background').
 */
export function installAutoLock(opts: { idleSeconds: number; lockOnBackground?: boolean }): void {
  if (installed) { armInactivity(opts.idleSeconds); return; }
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  installed = true;
  armInactivity(opts.idleSeconds);
  const activity = () => noteActivity();
  for (const ev of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
    document.addEventListener(ev, activity, { passive: true });
  }
  if (opts.lockOnBackground !== false) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') lockNow('background');
    });
    window.addEventListener('pagehide', () => lockNow('pagehide'));
  }
}

/** Sólo para tests: restablece el estado del módulo. */
export function __resetForTests(): void {
  wipes.clear();
  onLock = null;
  if (timer) { clearTimeout(timer); timer = null; }
  idleMs = 0;
  installed = false;
}
